import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  FiCheck, FiArrowRight, FiUsers, FiShield, 
  FiLayout, FiCode, FiHeadphones, FiServer, FiShare2, FiActivity,
  FiSearch, FiTarget, FiRefreshCw, FiLayers, FiBriefcase
} from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const About = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "About | ManagedSite.net";
    
    const description = "ManagedSite.net is a technical partner for websites, email, hosting, and support. Agencies and businesses can both start here.";
    const title = "About | ManagedSite.net";
    
    const updateMeta = (selector, attr, value) => {
      const el = document.querySelector(selector);
      if (el) el.setAttribute(attr, value);
    };
    
    updateMeta('meta[name="description"]', 'content', description);
    updateMeta('meta[property="og:title"]', 'content', title);
    updateMeta('meta[property="og:description"]', 'content', description);
    updateMeta('meta[property="twitter:title"]', 'content', title);
    updateMeta('meta[property="twitter:description"]', 'content', description);
  }, []);

  return (
    <div className="w-full">
      {/* Hero Section */}
      <section className="relative bg-[#FAF8F3] pt-24 pb-24 lg:pt-40 lg:pb-36 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <div className="text-sm font-bold tracking-[0.25em] text-[#D7B77A] uppercase mb-8">About</div>
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-extrabold text-[#17211E] tracking-tight leading-[1.1] mb-10 max-w-4xl mx-auto">
            The Technical Team Behind Your Website
          </h1>
          <p className="text-xl sm:text-2xl text-[#17211E]/80 leading-relaxed mb-12 max-w-3xl mx-auto font-medium">
            ManagedSite.net helps businesses and agencies with website management, technical support, hosting, email, and project work. You talk to the person responsible for the setup.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/digital-review" className="home-button">
              Request a Digital Review
            </Link>
            <Link to="/services" className="home-button-secondary">
              See Our Services
            </Link>
          </div>
        </div>
      </section>

      {/* Who you're working with */}
      <section className="py-24 bg-[#EAF1EC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid items-center gap-12 lg:grid-cols-[auto_1fr] lg:gap-16">
            <div className="h-40 w-40 overflow-hidden rounded-full mx-auto lg:mx-0 sm:h-48 sm:w-48">
              <img
                src="/dave-barrick.png?v=5"
                alt="Dave Barrick, founder of ManagedSite"
                className="h-full w-full object-cover"
              />
            </div>
            <div>
              <div className="text-sm font-bold tracking-[0.25em] text-[#D7B77A] uppercase mb-6">
                Who you&apos;re working with
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-[#17211E] mb-6 leading-tight">
                Hi, I&apos;m Dave.
              </h2>
              <p className="text-xl text-[#17211E]/75 leading-relaxed font-medium max-w-3xl">
                I&apos;ve spent my career hosting, supporting, and fixing websites. The company started in Indiana in 1996 as After Hours Data. ManagedSite.net is the same privately owned U.S. company. When you contact us, you&apos;re talking to me — the person responsible for making sure the work gets done.
              </p>
              <Link to="/contact" className="home-link mt-8 inline-block">
                Talk to Dave
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Who We Are */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-20">
            <h2 className="text-4xl md:text-5xl font-bold text-[#17211E] mb-8 leading-tight">One Partner for the Setup</h2>
            <p className="text-xl text-[#17211E]/70 leading-relaxed font-medium">
              Agencies can stay in front of their clients. Businesses can work with us directly. Either way, the website, email, hosting, and support have a clear owner.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
            {[
              { title: "Website management", icon: FiLayout },
              { title: "Technical support", icon: FiHeadphones },
              { title: "Managed hosting", icon: FiServer },
              { title: "Business email and deliverability", icon: FiShield },
              { title: "Design and development", icon: FiCode },
              { title: "Practical technology consulting", icon: FiShare2 }
            ].map((item, i) => (
              <div key={i} className="bg-[#FAF8F3] p-10 rounded-3xl border border-[#EAF1EC] flex items-center gap-6">
                <div className="bg-white p-4 rounded-2xl text-[#174C3C] shadow-sm flex-shrink-0">
                  <SafeIcon icon={item.icon} className="w-7 h-7" />
                </div>
                <h3 className="text-xl font-bold text-[#17211E] leading-snug">{item.title}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Brand Relationship */}
      <section className="py-24 bg-[#EAF1EC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-20">
            <h2 className="text-4xl md:text-5xl font-bold text-[#17211E] mb-8">Who We Work With</h2>
            <p className="text-xl text-[#17211E]/75 leading-relaxed font-medium">
              Agencies and businesses can both start here. The work is the same idea: one technical partner responsible for the setup.
            </p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 mb-16">
            <div className="bg-white p-12 rounded-[2.5rem] border border-[#3B7C6D]/10 shadow-sm flex flex-col h-full">
              <div className="text-sm font-bold tracking-[0.25em] text-[#D7B77A] uppercase mb-8">Agencies</div>
              <ul className="space-y-6 mb-12 flex-grow">
                {[
                  "White-label website management and support",
                  "Your agency stays the client-facing provider",
                  "Overflow help without hiring a full technical bench"
                ].map((item, i) => (
                  <li key={i} className="flex items-start text-xl font-bold text-[#17211E]">
                    <SafeIcon icon={FiCheck} className="w-7 h-7 text-[#174C3C] mr-4 flex-shrink-0 mt-0.5" />
                    {item}
                  </li>
                ))}
              </ul>
              <Link to="/agency-plans" className="home-button w-full">
                Explore Agency Plans
              </Link>
            </div>

            <div className="bg-white p-12 rounded-[2.5rem] border border-[#3B7C6D]/10 shadow-sm flex flex-col h-full">
              <div className="text-sm font-bold tracking-[0.25em] text-[#D7B77A] uppercase mb-8">Businesses</div>
              <ul className="space-y-6 mb-12 flex-grow">
                {[
                  "Website, email, hosting and technical help for your own business",
                  "One person responsible for the setup instead of several vendors",
                  "Start with one problem or a broader relationship"
                ].map((item, i) => (
                  <li key={i} className="flex items-start text-xl font-bold text-[#17211E]">
                    <SafeIcon icon={FiCheck} className="w-7 h-7 text-[#3B7C6D] mr-4 flex-shrink-0 mt-0.5" />
                    {item}
                  </li>
                ))}
              </ul>
              <Link to="/digital-review" className="home-button-secondary w-full">
                Tell us what you need
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* How We Work */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-20">
            <h2 className="text-4xl md:text-5xl font-bold text-[#17211E] mb-8 leading-tight">Practical, Organized and Accountable</h2>
            <p className="text-xl text-[#17211E]/75 leading-relaxed font-medium">
              We focus on clear communication, stable systems, and technical work that has a named owner.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
            {[
              { title: "Understand the Environment", text: "We review the website, hosting, email, plugins, risks and operating needs." },
              { title: "Recommend a Clear Path", text: "We help choose the right management level, project scope or support approach." },
              { title: "Do the Technical Work", text: "We handle the setup. Agencies stay the client-facing provider unless you want us in front." },
              { title: "Improve Over Time", text: "We monitor, maintain, report and recommend improvements when needed." }
            ].map((step, i) => (
              <div key={i} className="relative">
                <div className="text-6xl font-black text-[#EAF1EC] mb-8">{i + 1}</div>
                <h3 className="text-2xl font-bold text-[#17211E] mb-5">{step.title}</h3>
                <p className="text-lg text-[#17211E]/75 leading-relaxed font-medium">{step.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who We Serve */}
      <section className="py-24 bg-[#FAF8F3]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-[#17211E] mb-16 text-center">Who ManagedSite.net Is For</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 mb-16">
            {[
              { title: "Agencies and Designers", text: "Teams that need white-label website management, support and technical overflow." },
              { title: "Marketing and IT Consultants", text: "Providers who want a dependable technical partner for client websites and systems." },
              { title: "Businesses", text: "Website, email, hosting and technical help for businesses that want one person responsible for the setup." }
            ].map((item, i) => (
              <div key={i} className="bg-white p-12 rounded-[2.5rem] border border-[#EAF1EC] shadow-sm flex flex-col text-center">
                <h3 className="text-2xl font-extrabold text-[#17211E] mb-6">{item.title}</h3>
                <p className="text-lg text-[#17211E]/70 leading-relaxed font-medium mb-8 flex-grow">{item.text}</p>
              </div>
            ))}
          </div>
          <div className="text-center">
            <Link to="/contact" className="inline-flex items-center text-[#174C3C] font-extrabold hover:text-[#3B7C6D] transition-colors text-xl gap-3">
              Tell us what you need help with <SafeIcon icon={FiArrowRight} />
            </Link>
          </div>
        </div>
      </section>

      {/* Focus Area Grid */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-4xl md:text-5xl font-bold text-[#17211E] mb-8 leading-tight">The Same Technical Platform for Both</h2>
            <p className="text-xl text-[#17211E]/75 mb-20 font-medium max-w-3xl mx-auto">
              Website management, support, hosting, email, and project work — whether you run an agency or your own business.
            </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-20">
            {[
              "Website management", "Technical support", "Managed hosting", "Business email",
              "Web design and development", "Technology consulting", "Security and monitoring", "Migrations and recovery support"
            ].map((item, i) => (
              <div key={i} className="bg-[#FAF8F3] p-8 rounded-2xl border border-[#EAF1EC] font-bold text-[#17211E] text-center shadow-sm">
                {item}
              </div>
            ))}
          </div>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link to="/services" className="home-button">
              Explore All Services
            </Link>
            <Link to="/agency-plans" className="home-button-secondary">
              View Agency Plans
            </Link>
          </div>
        </div>
      </section>

      {/* Trust Principles */}
      <section className="py-24 bg-[#EAF1EC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-20">
            <h2 className="text-4xl md:text-5xl font-bold text-[#17211E] mb-8 leading-tight">How We Protect Agency Relationships</h2>
            <p className="text-xl text-[#17211E]/75 leading-relaxed font-medium">
              Agencies trust us with client infrastructure. That trust depends on clear boundaries and dependable execution.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            {[
              { title: "Your Clients Stay Your Clients", text: "We do not solicit your clients." },
              { title: "Clear Scope", text: "Maintenance, support and project work are defined clearly so expectations stay aligned." },
              { title: "Documented Delivery", text: "Important changes and recommendations are communicated in a way your team can use." },
              { title: "Long-Term Reliability", text: "We prioritize stable systems over quick shortcuts that create future problems." }
            ].map((item, i) => (
              <div key={i} className="bg-white p-12 rounded-[2.5rem] border border-[#3B7C6D]/10 shadow-sm">
                <h3 className="text-2xl font-extrabold text-[#174C3C] mb-6 leading-tight">{item.title}</h3>
                <p className="text-lg text-[#17211E]/75 leading-relaxed font-medium">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-32 bg-[#174C3C] text-center relative overflow-hidden text-white">
        <div className="absolute top-0 left-0 w-64 h-64 bg-white/5 rounded-full -translate-x-1/2 -translate-y-1/2"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-white/5 rounded-full translate-x-1/3 translate-y-1/3"></div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-10 tracking-tight leading-tight">
            Let’s Get the Technical Side Under Control
          </h2>
          <p className="text-2xl text-[#FAF8F3]/80 mb-14 max-w-3xl mx-auto leading-relaxed font-medium">
            Start with one problem or a longer relationship. Website management, support, hosting, email, and project work are all here.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4 mb-14">
            <Link to="/digital-review" className="home-button-on-dark">
              Request a Digital Review
            </Link>
            <Link to="/agency-services" className="home-button-on-dark-secondary">
              See Agency Services
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;